import { GrpcWebFetchTransport } from "@protobuf-ts/grpcweb-transport";
import { TextToSpeechRequest, SynthesizedSpeech } from "generated/tts";
import { TextToSpeechClient } from "generated/tts.client";
import type { RpcOptions } from "@protobuf-ts/runtime-rpc";
import { selectedValuesAtom } from "atoms";
import { useAtomValue } from "jotai";
import { getTTSUrl } from "shared/utils/tts";
import { AudioFormat_Encoding } from "@generated/audio_format";
import { isQuotaExhaustedError } from "shared/utils/grpc-error-handler";

/**
 * Custom error class for quota exhausted scenarios
 */
class QuotaExhaustedError extends Error {
  public readonly originalError: unknown;

  constructor(originalError: unknown) {
    super("QUOTA_EXHAUSTED");
    this.name = "QuotaExhaustedError";
    this.originalError = originalError;
  }
}

interface StreamCallbacks {
  onChunk: (pcmData: Uint8Array, isFirst: boolean) => void;
  onEnd: () => void;
  onError: (error: Error) => void;
}

interface TTSRequestCache {
  key: string;
  response: SynthesizedSpeech;
}

let lastRequestCache: TTSRequestCache | null = null;

function buildCacheKey(
  baseUrl: string,
  request: TextToSpeechRequest
): string {
  return JSON.stringify({
    baseUrl,
    environment: request.environment,
    flavor: request.flavor,
    text: request.text,
    language: request.language,
    voice: request.voice,
    samplerate: request.audioFormat?.samplerate,
    encoding: request.audioFormat?.encoding,
    speechRate: request.speechRate,
  });
}

export function useTTSGRPCService() {
  const {
    selectedLanguage,
    selectedVoice,
    selectedSampleRate,
    selectedEnv,
    selectedDeployment,
  } = useAtomValue(selectedValuesAtom);

  // Build a request object for cache lookups
  const buildRequest = (
    text: string,
    customSampleRate: string = "",
    voiceOverride?: string,
    languageOverride?: string
  ): TextToSpeechRequest => ({
    environment: selectedEnv,
    flavor: selectedDeployment,
    text,
    language: languageOverride || selectedLanguage,
    voice: voiceOverride || selectedVoice,
    audioFormat: {
      encoding: AudioFormat_Encoding.WAV,
      samplerate: +(customSampleRate || selectedSampleRate),
      bitrate: 0,
    },
    speechRate: 1.0,
    debug: false,
  });

  // Check if we already have a cached response for this request
  const checkCache = (
    text: string,
    customSampleRate: string = "",
    voiceOverride?: string,
    languageOverride?: string
  ): SynthesizedSpeech | null => {
    const baseUrl = getTTSUrl(selectedEnv);
    const request = buildRequest(text, customSampleRate, voiceOverride, languageOverride);
    const cacheKey = buildCacheKey(baseUrl, request);
    if (lastRequestCache && lastRequestCache.key === cacheKey) {
      return lastRequestCache.response;
    }
    return null;
  };

  // Store a completed response in the cache
  const setCache = (
    text: string,
    response: SynthesizedSpeech,
    customSampleRate: string = "",
    voiceOverride?: string,
    languageOverride?: string
  ): void => {
    const baseUrl = getTTSUrl(selectedEnv);
    const request = buildRequest(text, customSampleRate, voiceOverride, languageOverride);
    const cacheKey = buildCacheKey(baseUrl, request);
    lastRequestCache = { key: cacheKey, response };
  };

  const synthesize = async (
    text: string,
    authToken: string,
    customSampleRate: string = "",
    voiceOverride?: string,
    languageOverride?: string
  ): Promise<{ response: SynthesizedSpeech; fromCache: boolean }> => {
    const baseUrl = getTTSUrl(selectedEnv);
    const transport = new GrpcWebFetchTransport({ baseUrl });
    const client = new TextToSpeechClient(transport);

    const finalSampleRate = customSampleRate || selectedSampleRate;

    const voiceToUse = voiceOverride || selectedVoice;
    const languageToUse = languageOverride || selectedLanguage;

    const request: TextToSpeechRequest = {
      environment: selectedEnv,
      flavor: selectedDeployment,
      text,
      language: languageToUse,
      voice: voiceToUse,
      audioFormat: {
        encoding: AudioFormat_Encoding.WAV,
        samplerate: +finalSampleRate,
        bitrate: 0,
      },
      speechRate: 1.0,
      debug: false,
    };

    const rpcOptions: RpcOptions = {
      meta: {
        Authorization: `Bearer ${authToken}`,
      },
    };

    const cacheKey = buildCacheKey(baseUrl, request);
    if (lastRequestCache && lastRequestCache.key === cacheKey) {
      return { response: lastRequestCache.response, fromCache: true };
    }

    try {
      const call = client.synthesize(request, rpcOptions);
      const audioChunks: Uint8Array[] = [];

      for await (const response of call.responses) {
        if (response.audio && response.audio.length > 0) {
          audioChunks.push(response.audio);
        }
      }

      const totalLength = audioChunks.reduce(
        (acc, chunk) => acc + chunk.length,
        0
      );
      const combinedAudio = new Uint8Array(totalLength);
      let offset = 0;

      for (const chunk of audioChunks) {
        combinedAudio.set(chunk, offset);
        offset += chunk.length;
      }

      const synthesizedSpeech: SynthesizedSpeech = {
        audio: combinedAudio,
        phoneme: "",
        debugInfo: undefined,
      };

      lastRequestCache = { key: cacheKey, response: synthesizedSpeech };

      return { response: synthesizedSpeech, fromCache: false };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("TTS synthesis error:", error);

      // Check if this is a quota exhausted error and enhance it with more context
      if (isQuotaExhaustedError(error)) {
        throw new QuotaExhaustedError(error);
      }

      throw error;
    }
  };

  const synthesizeStream = (
    text: string,
    authToken: string,
    callbacks: StreamCallbacks,
    customSampleRate: string = "",
    voiceOverride?: string,
    languageOverride?: string
  ): { cancel: () => void } => {
    const baseUrl = getTTSUrl(selectedEnv);
    const transport = new GrpcWebFetchTransport({ baseUrl });
    const client = new TextToSpeechClient(transport);

    const finalSampleRate = customSampleRate || selectedSampleRate;
    const voiceToUse = voiceOverride || selectedVoice;
    const languageToUse = languageOverride || selectedLanguage;

    const request: TextToSpeechRequest = {
      environment: selectedEnv,
      flavor: selectedDeployment,
      text,
      language: languageToUse,
      voice: voiceToUse,
      audioFormat: {
        encoding: AudioFormat_Encoding.WAV,
        samplerate: +finalSampleRate,
        bitrate: 0,
      },
      speechRate: 1.0,
      debug: false,
    };

    const rpcOptions: RpcOptions = {
      meta: {
        Authorization: `Bearer ${authToken}`,
      },
    };

    let cancelled = false;

    // Consume the gRPC stream in the background so we can return cancel immediately
    (async () => {
      try {
        const call = client.synthesize(request, rpcOptions);
        let isFirst = true;

        for await (const response of call.responses) {
          if (cancelled) break;

          if (response.audio && response.audio.length > 0) {
            callbacks.onChunk(response.audio, isFirst);
            isFirst = false;
          }
        }

        if (!cancelled) {
          callbacks.onEnd();
        }
      } catch (error) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error("TTS streaming error:", error);
          if (isQuotaExhaustedError(error)) {
            callbacks.onError(new QuotaExhaustedError(error));
          } else {
            callbacks.onError(error instanceof Error ? error : new Error(String(error)));
          }
        }
      }
    })();

    // Synchronous return - basically the user can cancel the playback anytime
    return {
      cancel: () => {
        cancelled = true;
      },
    };
  };

  return { synthesize, synthesizeStream, checkCache, setCache };

}